/**
 * 站点设置（07 功能 PRD §3.4：默认语言/主题/社交链接；决策 1 无收件邮箱配置项）
 * + 小镇场景资产路径。敏感值掩码由后端保证（本页不回显密钥类设置——白名单外不显示）。
 */
import { useCallback, useEffect, useState } from 'react';
import type { AdminSettings, AdminTownConfig } from '@chronospeak/contracts';
import { adminHttp, ApiError } from '../lib/api';
import { Button } from '@chronospeak/ui';
import { ErrorNote, Loading, PageHeader } from '../components/ui-bits';

interface SocialLink {
  label: string;
  url: string;
}

export function SettingsPage() {
  const [settings, setSettings] = useState<AdminSettings | null>(null);
  const [town, setTown] = useState<AdminTownConfig | null>(null);
  const [defaultLocale, setDefaultLocale] = useState('zh-CN');
  const [theme, setTheme] = useState('dark');
  const [socials, setSocials] = useState<SocialLink[]>([]);
  const [dayAsset, setDayAsset] = useState('');
  const [nightAsset, setNightAsset] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [s, t] = await Promise.all([
        adminHttp.get<AdminSettings>('/admin/settings'),
        adminHttp.get<AdminTownConfig>('/admin/town'),
      ]);
      setSettings(s);
      setTown(t);
      for (const item of s.items) {
        if (item.key === 'site.defaultLocale') setDefaultLocale(item.value);
        if (item.key === 'site.theme') setTheme(item.value);
        if (item.key === 'site.socialLinks') {
          try {
            setSocials(JSON.parse(item.value) as SocialLink[]);
          } catch {
            setSocials([]);
          }
        }
      }
      setDayAsset(t.dayAsset);
      setNightAsset(t.nightAsset);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '加载失败');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const saveSettings = async () => {
    setError(null);
    setNotice(null);
    try {
      await adminHttp.patch<AdminSettings>('/admin/settings', {
        items: [
          { key: 'site.defaultLocale', value: defaultLocale },
          { key: 'site.theme', value: theme },
          { key: 'site.socialLinks', value: JSON.stringify(socials.filter((s) => s.label && s.url)) },
        ],
      });
      setNotice('站点设置已保存。');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '保存失败');
    }
  };

  const saveTown = async () => {
    setError(null);
    setNotice(null);
    try {
      await adminHttp.patch<AdminTownConfig>('/admin/town', {
        dayAsset,
        nightAsset,
      });
      setNotice('小镇场景配置已保存（M3 场景渲染消费）。');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '保存失败');
    }
  };

  if (error && !settings) {
    return (
      <section>
        <PageHeader title='站点设置' />
        <ErrorNote message={error} />
      </section>
    );
  }
  if (!settings || !town) {
    return (
      <section>
        <PageHeader title='站点设置' />
        <Loading />
      </section>
    );
  }

  return (
    <section>
      <PageHeader title='站点设置' sub='设置项白名单：默认语言 / 主题 / 社交链接（决策 1：无收件邮箱配置）。' />
      {error && <ErrorNote message={error} />}
      {notice && (
        <p role='status' className='mgr-notice'>
          {notice}
        </p>
      )}

      <fieldset className='mgr-fieldset' style={{ maxWidth: 560 }}>
        <legend>站点</legend>
        <label className='mgr-field'>
          默认语言
          <select value={defaultLocale} onChange={(e) => setDefaultLocale(e.target.value)}>
            <option value='zh-CN'>zh-CN（中文）</option>
            <option value='en'>en（English）</option>
          </select>
        </label>
        <label className='mgr-field'>
          默认主题（内容页）
          <select value={theme} onChange={(e) => setTheme(e.target.value)}>
            <option value='dark'>深色（默认）</option>
            <option value='light'>浅色</option>
          </select>
        </label>
        <legend style={{ marginTop: 12 }}>社交链接</legend>
        {socials.map((s, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 8, marginBottom: 8 }}>
            <input
              value={s.label}
              placeholder='名称'
              onChange={(e) => setSocials(socials.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
            />
            <input
              value={s.url}
              placeholder='https://…'
              onChange={(e) => setSocials(socials.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))}
            />
            <Button variant='ghost' size='sm' onClick={() => setSocials(socials.filter((_, j) => j !== i))}>
              删除
            </Button>
          </div>
        ))}
        <Button variant='ghost' size='sm' onClick={() => setSocials([...socials, { label: '', url: '' }])}>
          + 添加链接
        </Button>
        <div style={{ marginTop: 12 }}>
          <Button variant='primary' size='sm' onClick={() => void saveSettings()}>
            保存站点设置
          </Button>
        </div>
      </fieldset>

      <fieldset className='mgr-fieldset' style={{ maxWidth: 560, marginTop: 24 }}>
        <legend>小镇场景（M3 渲染消费）</legend>
        <label className='mgr-field'>
          白天底图路径
          <input value={dayAsset} onChange={(e) => setDayAsset(e.target.value)} />
        </label>
        <label className='mgr-field'>
          夜晚底图路径
          <input value={nightAsset} onChange={(e) => setNightAsset(e.target.value)} />
        </label>
        <p style={{ fontSize: 12, color: 'var(--muted)', margin: '4px 0 8px' }}>
          当前场景版本：{town.sceneVersion} · 建筑入口 {town.entrances.length} 项 · NPC{' '}
          {town.npcs.length} 个（人物在创作人页维护）。
        </p>
        <Button variant='primary' size='sm' onClick={() => void saveTown()}>
          保存场景配置
        </Button>
      </fieldset>
    </section>
  );
}
